import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { tap } from "rxjs";
import { EnigmeLightDTO } from "../models/enigmeLightDTO.model";
import { EnigmesService } from "../services/enigmes.service";
import { FilterCtfAction, GetCtfAction } from "./ctf.actions";
import { CtfStatusEnum } from "./ctf-status.enum";
import { TagEnum } from "../enums/tag.enum";

export interface CtfStateModel {
  enigme: EnigmeLightDTO[];
  enigmeFiltree: EnigmeLightDTO[];
  tagsDistinct: TagEnum[];
  nextEnigmeAvailable: Date | null;
}

@State<CtfStateModel>({
  name: "CtfStore",
  defaults: {
    enigme: [],
    enigmeFiltree: [],
    tagsDistinct: [],
    nextEnigmeAvailable: null,
  },
})
@Injectable()
export class CtfState {
  constructor(private service: EnigmesService) {}

  static ctfComparator = (ctf1: EnigmeLightDTO, ctf2: EnigmeLightDTO) =>
    CtfState.ctfPriority(ctf2) - CtfState.ctfPriority(ctf1);

  @Selector()
  static selectCtfs(state: CtfStateModel) {
    return state.enigmeFiltree.sort(CtfState.ctfComparator);
  }

  @Selector()
  static selectNextEnigmeAvailable(state: CtfStateModel) {
    return state.nextEnigmeAvailable;
  }

  @Selector()
  static selectCtfsUser(state: CtfStateModel) {
    return state.enigme.filter((e) => e.dateHeureDebut);
  }

  @Selector()
  static selectCtfsUserInProgress(state: CtfStateModel) {
    return state.enigme.filter((e) => e.dateHeureDebut && !e.dateHeureFin);
  }
  @Selector()
  static selectHighlightCtfs(state: CtfStateModel) {
    return state.enigme.filter((e) => e.highlights && !e.dateHeureFin);
  }

  /**
   * State selector for distinct tags
   * @param state CtfStateModel
   * @returns each tag in order a -> z
   */

  @Selector()
  static selectDistinctTags(state: CtfStateModel) {
    return state.tagsDistinct;
  }

  /**
   * Calcule la priorité du CTF pour le tri
   * En premier : les CTF démarrés
   * En dernier : les CTF terminés
   * @param enigme
   * @private
   */
  private static ctfPriority(enigme: EnigmeLightDTO): number {
    if (enigme.isNew && !enigme.dateHeureDebut && !enigme.dateHeureFin) {
      return 3;
    }
    if (enigme.dateHeureDebut && !enigme.dateHeureFin) {
      return 2;
    }
    if (enigme.dateHeureDebut && enigme.dateHeureFin) {
      return 0;
    }
    return 1;
  }

  /**
   * State action for all enigmes
   * @param ctx StateContext<CtfStateModel>
   * @returns all enigme and enigme filtered and distincts tags
   */
  @Action(GetCtfAction)
  getCtfAction(ctx: StateContext<CtfStateModel>) {
    return this.service.getAllEnigmes().pipe(
      tap((enigmeFetched) => {
        const state = ctx.getState();
        if (enigmeFetched.enigmeList?.length > 0) {
          enigmeFetched.enigmeList.map((e) => {
            if (e.availableOn) {
              const now = new Date();
              const offset = now.getTimezoneOffset();
              const nowUTC = new Date(now).getTime() + offset * 60000;
              const difference = nowUTC - new Date(e.availableOn).getTime();
              e.isNew = difference < 86400000 * 3;
            }
          });
          ctx.setState({
            ...state,
            enigme: enigmeFetched.enigmeList,
            enigmeFiltree: enigmeFetched.enigmeList,
            tagsDistinct: [
              ...new Set(
                enigmeFetched.enigmeList.map((item) => item.tags).flat()
              ),
            ],
            nextEnigmeAvailable: enigmeFetched.nextEnigmeAvailable
              ? new Date(enigmeFetched.nextEnigmeAvailable)
              : null,
          });
        }
      })
    );
  }

  /**
   * State Action to filter enigme with tag, difficulty and status
   * @param ctx State context
   * @param payload payload
   * @returns filtered enigme depending on tag, difficulty, status or all combined
   */
  @Action(FilterCtfAction)
  filterCtfAction(
    ctx: StateContext<CtfStateModel>,
    { payload }: FilterCtfAction
  ) {
    const selectedTheme = payload.selectTheme;
    const selectedDifficulty = payload.selectDifficulty;
    const selectedStatus = payload.selectStatus;

    const enigmes = ctx.getState().enigme;

    let filteredEnigmes = [...enigmes];

    if (selectedTheme !== "Tous") {
      filteredEnigmes = filteredEnigmes.filter((enigme) =>
        enigme.tags.includes(selectedTheme)
      );
    }

    if (selectedDifficulty !== "Tous") {
      filteredEnigmes = filteredEnigmes.filter(
        (enigme) => enigme.difficulty === selectedDifficulty
      );
    }

    if (selectedStatus !== "Tous") {
      filteredEnigmes = filteredEnigmes.filter((enigme) => {
        if (selectedStatus === CtfStatusEnum.NON_DEMARRE) {
          return enigme.dateHeureDebut === null;
        } else if (selectedStatus === CtfStatusEnum.EN_COURS) {
          return enigme.dateHeureDebut !== null && enigme.dateHeureFin === null;
        }
        return enigme.dateHeureFin !== null;
      });
    }

    ctx.patchState({ enigmeFiltree: filteredEnigmes });
  }
}
